import React, { useState } from 'react';
import { Plus, Save, Trash2, Copy, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HAZIDWorksheet = ({ worksheet }) => {
  const navigate = useNavigate();
  const [hazards, setHazards] = useState([
    {
      id: 1,
      node: 'HE-101',
      equipmentTask: 'Heat Exchanger - Tube Cleaning',
      chemical: 'Process Fluid (Flammable)',
      deviation: 'High Temperature',
      cause: 'Insufficient cooling time before maintenance',
      consequence: 'Burns, thermal injury to personnel',
      existingSafeguards: ['Cool-down procedure', 'Temperature monitoring', 'PPE requirements'],
      likelihood: 3,
      severity: 5,
      riskRating: 15,
      recommendations: ['Extend cool-down period to 4 hours', 'Install temperature indicators', 'Mandatory IR scanning before access'],
      status: 'open'
    },
    {
      id: 2,
      node: 'HE-101',
      equipmentTask: 'Heat Exchanger - Tube Cleaning',
      chemical: 'Cleaning Chemicals',
      deviation: 'Chemical Exposure',
      cause: 'Improper draining of process fluids',
      consequence: 'Chemical burns, inhalation hazard',
      existingSafeguards: ['Drain and flush procedure', 'Chemical resistant PPE', 'Gas testing'],
      likelihood: 4,
      severity: 4,
      riskRating: 16,
      recommendations: ['Triple flush verification', 'Continuous air monitoring', 'Emergency eyewash station nearby'],
      status: 'open'
    }
  ]);

  const [newHazard, setNewHazard] = useState({
    node: '',
    equipmentTask: '',
    chemical: '',
    deviation: '',
    cause: '',
    consequence: '',
    existingSafeguards: [],
    likelihood: 1,
    severity: 1,
    recommendations: [],
    status: 'open'
  });

  const deviationTypes = [
    'No Flow', 'High Flow', 'Low Flow', 'Reverse Flow',
    'High Pressure', 'Low Pressure', 'High Temperature', 'Low Temperature',
    'High Level', 'Low Level', 'Contamination', 'Composition Change',
    'Corrosion', 'Erosion', 'Vibration', 'Leak'
  ];

  const handleAddHazard = () => {
    const hazard = {
      ...newHazard,
      id: hazards?.length + 1,
      riskRating: newHazard?.likelihood * newHazard?.severity
    };
    setHazards([...hazards, hazard]);
    setNewHazard({
      node: '',
      equipmentTask: '',
      chemical: '',
      deviation: '',
      cause: '',
      consequence: '',
      existingSafeguards: [],
      likelihood: 1,
      severity: 1,
      recommendations: [],
      status: 'open'
    });
  };

  const handleDeleteHazard = (id) => {
    setHazards(hazards?.filter(h => h?.id !== id));
  };

  const handleNewHazardEntry = () => {
    navigate('/risk-identification');
  };

  const getRiskColor = (rating) => {
    if (rating >= 20) return 'bg-red-600 text-white';
    if (rating >= 15) return 'bg-orange-500 text-white';
    if (rating >= 10) return 'bg-yellow-400 text-gray-900';
    if (rating >= 5) return 'bg-green-400 text-gray-900';
    return 'bg-green-200 text-gray-900';
  };

  if (!worksheet) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Select a worksheet to begin</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Worksheet Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{worksheet?.title}</h2>
            <p className="text-sm text-gray-600 mt-1">Equipment: {worksheet?.equipment}</p>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={handleNewHazardEntry}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="w-4 h-4" />
              New hazard entry
            </button>
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Save className="w-4 h-4" />
              Save Worksheet
            </button>
          </div>
        </div>
      </div>
      {/* Hazards Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Node</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Equipment/Task</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Chemical</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Deviation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Cause</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Consequence</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Safeguards</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">L</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">S</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Risk</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {hazards?.map((hazard) => (
                <tr key={hazard?.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{hazard?.node}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{hazard?.equipmentTask}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{hazard?.chemical}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-md text-xs font-medium">
                      {hazard?.deviation}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">{hazard?.cause}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{hazard?.consequence}</td>
                  <td className="px-4 py-3 text-sm">
                    <ul className="list-disc list-inside text-gray-700">
                      {hazard?.existingSafeguards?.slice(0, 2)?.map((sg, idx) => (
                        <li key={idx}>{sg}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="font-semibold text-gray-900">{hazard?.likelihood}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className="font-semibold text-gray-900">{hazard?.severity}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 rounded-md text-sm font-bold ${getRiskColor(hazard?.riskRating)}`}>
                      {hazard?.riskRating}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDeleteHazard(hazard?.id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button className="p-1 text-gray-600 hover:bg-gray-100 rounded">
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add New Hazard Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Hazard</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Node/Equipment Tag</label>
            <input
              type="text"
              value={newHazard?.node}
              onChange={(e) => setNewHazard({ ...newHazard, node: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., HE-101"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Equipment/Task</label>
            <input
              type="text"
              value={newHazard?.equipmentTask}
              onChange={(e) => setNewHazard({ ...newHazard, equipmentTask: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chemical/Substance</label>
            <input
              type="text"
              value={newHazard?.chemical}
              onChange={(e) => setNewHazard({ ...newHazard, chemical: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deviation Type</label>
            <select
              value={newHazard?.deviation}
              onChange={(e) => setNewHazard({ ...newHazard, deviation: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select deviation...</option>
              {deviationTypes?.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Cause</label>
            <textarea
              value={newHazard?.cause}
              onChange={(e) => setNewHazard({ ...newHazard, cause: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Consequence</label>
            <textarea
              value={newHazard?.consequence}
              onChange={(e) => setNewHazard({ ...newHazard, consequence: e?.target?.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood (1-8)</label>
            <input
              type="number"
              min="1"
              max="8"
              value={newHazard?.likelihood}
              onChange={(e) => setNewHazard({ ...newHazard, likelihood: parseInt(e?.target?.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Severity (1-8)</label>
            <input
              type="number"
              min="1"
              max="8"
              value={newHazard?.severity}
              onChange={(e) => setNewHazard({ ...newHazard, severity: parseInt(e?.target?.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <button
          onClick={handleAddHazard}
          className="mt-4 flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <Plus className="w-4 h-4" />
          Add Hazard
        </button>
      </div>
    </div>
  );
};

export default HAZIDWorksheet;